// =============================================================================
// ShopifyFulfillmentPanel
// =============================================================================
// Platform admin form for the singleton ShopifyFulfillmentDefault. Drives the
// initial install behaviour for every Shopify connection and, when the admin
// clicks "Run Backfill", triggers a one-shot migration of legacy connections.
//
// UX guarantees (match server-side policy):
//   • Changes to defaults NEVER overwrite existing merchants (no_overwrite).
//     A prominent info banner makes this explicit.
//   • Per-connection resync is a separate, merchant-scoped action surfaced
//     on the Tenant Detail page (not in this panel).
// =============================================================================

import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, Input, Spinner } from '@tkpod/ui';

import { api } from '@/lib/api';

interface ShopifyFulfillmentDefault {
  id: string;
  locationName: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  provinceCode: string;
  countryCode: string;
  zip: string;
  phone: string | null;
  profileName: string;
  zoneName: string;
  flatRateName: string;
  flatRateAmount: string;
  currencyCode: string;
  serviceName: string;
  enabled: boolean;
  updatedByAdminUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BackfillJob {
  id: string;
  scope: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  totalCount: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

interface GetDefaultsResponse {
  data: ShopifyFulfillmentDefault;
}
interface ListBackfillResponse {
  data: BackfillJob[];
}
interface StartBackfillResponse {
  data: BackfillJob;
}

type FormState = Omit<ShopifyFulfillmentDefault, 'id' | 'createdAt' | 'updatedAt' | 'updatedByAdminUserId'>;

function toFormState(row: ShopifyFulfillmentDefault): FormState {
  return {
    locationName: row.locationName,
    addressLine1: row.addressLine1,
    addressLine2: row.addressLine2 ?? '',
    city: row.city,
    provinceCode: row.provinceCode,
    countryCode: row.countryCode,
    zip: row.zip,
    phone: row.phone ?? '',
    profileName: row.profileName,
    zoneName: row.zoneName,
    flatRateName: row.flatRateName,
    flatRateAmount: row.flatRateAmount,
    currencyCode: row.currencyCode,
    serviceName: row.serviceName,
    enabled: row.enabled,
  };
}

export function ShopifyFulfillmentPanel() {
  const queryClient = useQueryClient();

  // ---- Defaults (singleton) ----------------------------------------------
  const { data, isLoading, error } = useQuery<GetDefaultsResponse>({
    queryKey: ['admin', 'shopify', 'fulfillment-default'],
    queryFn: () => api.get<GetDefaultsResponse>('/api/v1/admin/shopify/fulfillment-default'),
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  // Hydrate the form when the singleton row first loads. We intentionally
  // don't overwrite local edits on subsequent refetches — if the admin wants
  // a fresh copy they can hit the "Reset" button.
  useEffect(() => {
    if (form === null && data?.data) {
      setForm(toFormState(data.data));
    }
  }, [data, form]);

  const updateMut = useMutation({
    mutationFn: async (body: Partial<FormState>) =>
      api.put<GetDefaultsResponse>('/api/v1/admin/shopify/fulfillment-default', body),
    onSuccess: async (res) => {
      setForm(toFormState(res.data));
      setSaveError(null);
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'shopify', 'fulfillment-default'],
      });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to save defaults';
      setSaveError(msg);
    },
  });

  // ---- Backfill ----------------------------------------------------------
  const { data: backfillList } = useQuery<ListBackfillResponse>({
    queryKey: ['admin', 'shopify', 'backfill'],
    queryFn: () =>
      api.get<ListBackfillResponse>('/api/v1/admin/shopify/backfill?limit=5'),
    refetchInterval: (q) => {
      const rows = (q.state.data as ListBackfillResponse | undefined)?.data ?? [];
      return rows.some((r) => r.status === 'PENDING' || r.status === 'RUNNING')
        ? 5_000
        : false;
    },
  });

  const startBackfillMut = useMutation({
    mutationFn: () =>
      api.post<StartBackfillResponse>('/api/v1/admin/shopify/backfill', {}),
    onSuccess: async () => {
      setBackfillError(null);
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'shopify', 'backfill'],
      });
    },
    onError: (err: unknown) => {
      setBackfillError(
        err instanceof Error ? err.message : 'Failed to start backfill',
      );
    },
  });

  // -----------------------------------------------------------------------

  if (isLoading || !form) {
    return (
      <Card>
        <CardHeader
          title="Shopify Fulfillment"
          description="Platform-wide defaults applied to every Shopify store on install."
        />
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader
          title="Shopify Fulfillment"
          description="Platform-wide defaults applied to every Shopify store on install."
        />
        <div className="mt-4 rounded-md bg-error-muted p-4 text-sm text-error">
          Failed to load: {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  const onChange =
    (key: keyof FormState) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const value =
        e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form) return;
    setSaveError(null);
    updateMut.mutate({
      ...form,
      flatRateAmount: Number.parseFloat(form.flatRateAmount).toString(),
      addressLine2: form.addressLine2 || null,
      phone: form.phone || null,
    });
  };

  const runningBackfill = backfillList?.data.find(
    (j) => j.status === 'PENDING' || j.status === 'RUNNING',
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Shopify Fulfillment"
          description="Applied automatically when a merchant installs the TK POD Shopify app. Existing stores keep their customisations (see notice below)."
        />

        <div className="mt-4 rounded-md border border-info-border bg-info-muted p-4 text-sm text-info">
          <strong className="font-semibold">no_overwrite policy:</strong>{' '}
          Changes to these defaults apply only to <em>new</em> installs. Existing
          merchants keep whatever they last had (including any manual edits
          they made inside Shopify). To push these defaults to one specific
          store, use the "Re-sync" button on that store's tenant detail page.
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-6">
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-text-primary">
              FulfillmentService
            </legend>
            <Input
              label="Service name"
              value={form.serviceName}
              onChange={onChange('serviceName')}
              required
            />
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-text-primary">
              Location
            </legend>
            <Input
              label="Location name"
              value={form.locationName}
              onChange={onChange('locationName')}
              required
            />
            <Input
              label="Address line 1"
              value={form.addressLine1}
              onChange={onChange('addressLine1')}
              required
            />
            <Input
              label="Address line 2 (optional)"
              value={form.addressLine2 ?? ''}
              onChange={onChange('addressLine2')}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="City"
                value={form.city}
                onChange={onChange('city')}
                required
              />
              <Input
                label="State (ISO)"
                value={form.provinceCode}
                onChange={onChange('provinceCode')}
                required
                maxLength={5}
              />
              <Input
                label="ZIP"
                value={form.zip}
                onChange={onChange('zip')}
                required
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Country (ISO-2)"
                value={form.countryCode}
                onChange={onChange('countryCode')}
                required
                maxLength={2}
              />
              <Input
                label="Phone (optional)"
                value={form.phone ?? ''}
                onChange={onChange('phone')}
              />
            </div>
          </fieldset>

          {/* DeliveryProfile seed fields (profileName, zoneName, flatRateName,
              flatRateAmount, currencyCode) are intentionally hidden: ongoing
              rates are managed in the "Shipping zones & methods" panel, which
              overrides the seed flat rate on the first sync. The values stay
              in form state and are submitted unchanged so the PUT contract and
              install-time seeding keep working. */}

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-text-primary">
              Kill switch
            </legend>
            <label className="inline-flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={onChange('enabled')}
              />
              Auto-setup enabled for new installs
            </label>
            <p className="text-xs text-text-tertiary">
              Disable to skip the Shopify auto-setup on new installs. Existing
              stores are unaffected. Use sparingly — merchants will not see
              the TK shipping profile until you re-enable.
            </p>
          </fieldset>

          {saveError ? (
            <div className="rounded-md bg-error-muted p-3 text-sm text-error">
              {saveError}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                data?.data ? setForm(toFormState(data.data)) : undefined
              }
              disabled={updateMut.isPending}
            >
              Reset
            </Button>
            <Button type="submit" disabled={updateMut.isPending}>
              {updateMut.isPending ? 'Saving…' : 'Save defaults'}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <CardHeader
          title="Legacy connection backfill"
          description="One-shot migration for stores installed before the auto-setup scopes were introduced. Safe to run repeatedly — it is idempotent."
        />

        <div className="mt-4 space-y-4">
          {runningBackfill ? (
            <div className="rounded-md border border-info-border bg-info-muted p-4 text-sm text-info">
              <div className="font-semibold">
                Backfill in progress ({runningBackfill.status})
              </div>
              <div className="mt-1">
                Processed {runningBackfill.processedCount} of{' '}
                {runningBackfill.totalCount} · success{' '}
                {runningBackfill.successCount} · failed{' '}
                {runningBackfill.failedCount}
              </div>
            </div>
          ) : null}

          {backfillError ? (
            <div className="rounded-md bg-error-muted p-3 text-sm text-error">
              {backfillError}
            </div>
          ) : null}

          <div className="flex items-center justify-end">
            <Button
              type="button"
              onClick={() => startBackfillMut.mutate()}
              disabled={
                !!runningBackfill || startBackfillMut.isPending
              }
            >
              {startBackfillMut.isPending
                ? 'Queuing…'
                : runningBackfill
                ? 'Already running'
                : 'Run backfill'}
            </Button>
          </div>

          {backfillList && backfillList.data.length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-border-subtle">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-subtle text-xs uppercase text-text-tertiary">
                  <tr>
                    <th className="px-3 py-2 text-left">Started</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Processed</th>
                    <th className="px-3 py-2 text-right">Success</th>
                    <th className="px-3 py-2 text-right">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {backfillList.data.map((j) => (
                    <tr key={j.id} className="border-t border-border-subtle">
                      <td className="px-3 py-2">
                        {j.startedAt
                          ? new Date(j.startedAt).toLocaleString()
                          : new Date(j.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">{j.status}</td>
                      <td className="px-3 py-2 text-right">
                        {j.processedCount}/{j.totalCount}
                      </td>
                      <td className="px-3 py-2 text-right">{j.successCount}</td>
                      <td className="px-3 py-2 text-right">{j.failedCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
